import {
  CHUNK_HEADER_SIZE,
  CHUNK_TYPE_DOCUMENT,
  CHUNK_TYPE_HEADER,
  SCBF_MAGIC,
  SCBF_VERSION,
  STATIC_HEADER_SIZE,
} from 'src/utils/collaboration/scbf/constants';

export interface SCBFEncodeOptions {
  /** The Y.js state update bytes (Document chunk payload) */
  documentPayload: Uint8Array;
  /** Optional key-value metadata headers */
  headers?: Record<string, string>;
}

const textEncoder = new TextEncoder();

/**
 * Encode data into an SCBF binary buffer.
 *
 * @param options - The document payload and optional headers
 * @returns The encoded SCBF buffer as Uint8Array
 */
export const encodeSCBF = (options: SCBFEncodeOptions): Uint8Array => {
  const { documentPayload, headers = {} } = options;

  // Build header chunks data
  const headerEntries = Object.entries(headers);
  const headerChunkBuffers: Uint8Array[] = [];

  headerEntries.forEach(([name, value]) => {
    const nameBytes = textEncoder.encode(name);
    const valueBytes = textEncoder.encode(value);
    // Header chunk data: nameLength(4) + name + valueLength(4) + value
    const chunkDataSize = 4 + nameBytes.byteLength + 4 + valueBytes.byteLength;
    const chunkData = new Uint8Array(chunkDataSize);
    const chunkDataView = new DataView(chunkData.buffer);

    let offset = 0;
    chunkDataView.setUint32(offset, nameBytes.byteLength, false);
    offset += 4;
    chunkData.set(nameBytes, offset);
    offset += nameBytes.byteLength;
    chunkDataView.setUint32(offset, valueBytes.byteLength, false);
    offset += 4;
    chunkData.set(valueBytes, offset);

    headerChunkBuffers.push(chunkData);
  });

  // Total chunk count: header chunks + 1 document chunk
  const chunkCount = headerChunkBuffers.length + 1;

  // Calculate total buffer size
  let totalSize = STATIC_HEADER_SIZE;
  headerChunkBuffers.forEach((chunkData) => {
    totalSize += CHUNK_HEADER_SIZE + chunkData.byteLength;
  });
  totalSize += CHUNK_HEADER_SIZE + documentPayload.byteLength;

  // Assemble the buffer
  const buffer = new Uint8Array(totalSize);
  const view = new DataView(buffer.buffer);
  let pos = 0;

  // Static header: magic (4 bytes)
  buffer.set(SCBF_MAGIC, pos);
  pos += 4;

  // Static header: reserved (4 bytes, zeroed)
  pos += 4;

  // Static header: version (uint32 big-endian)
  view.setUint32(pos, SCBF_VERSION, false);
  pos += 4;

  // Static header: chunkCount (uint32 big-endian)
  view.setUint32(pos, chunkCount, false);
  pos += 4;

  // Header chunks first
  headerChunkBuffers.forEach((chunkData) => {
    // Chunk header: type (1 byte)
    buffer[pos] = CHUNK_TYPE_HEADER;
    pos += 1;
    // Chunk header: reserved (3 bytes, zeroed)
    pos += 3;
    // Chunk header: length (uint32 big-endian)
    view.setUint32(pos, chunkData.byteLength, false);
    pos += 4;
    // Chunk data
    buffer.set(chunkData, pos);
    pos += chunkData.byteLength;
  });

  // Document chunk
  buffer[pos] = CHUNK_TYPE_DOCUMENT;
  pos += 1;
  // Chunk header: reserved (3 bytes, zeroed)
  pos += 3;
  // Chunk header: length (uint32 big-endian)
  view.setUint32(pos, documentPayload.byteLength, false);
  pos += 4;
  // Chunk data
  buffer.set(documentPayload, pos);

  return buffer;
};
